/** @format */

import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext, useGlobalState } from "senf-shared";
import { useGetTranslations } from "senf-shared/utils/hooks";
import List from "../../core/list/List";
import ListItem from "../../core/list/ListItem";
import { useModals } from "../modalStack/ModalProvider";
import type { LanguageSettingsProps } from "./LanguageSettings.types";

interface LocaleInfo {
  icon: string;
  shortName: string;
  languageKey: string;
  name?: string;
}

interface Locales {
  de: LocaleInfo;
  en?: LocaleInfo;
  es?: LocaleInfo;
  latam?: LocaleInfo;
  fr?: LocaleInfo;
  tr?: LocaleInfo;
  ar?: LocaleInfo;
  uk?: LocaleInfo;
  ru?: LocaleInfo;
  pl?: LocaleInfo;
  cs?: LocaleInfo;
  it?: LocaleInfo;
  nl?: LocaleInfo;
}

export const locales: Locales = {
  de: {
    icon: "locales/de",
    shortName: "DE",
    languageKey: "de",
    name: "german",
  },
  en: {
    icon: "locales/en",
    shortName: "EN",
    languageKey: "en",
    name: "english",
  },
  es: {
    icon: "locales/es",
    shortName: "ES",
    languageKey: "es",
    name: "spanish",
  },
  latam: {
    icon: "locales/latam",
    shortName: "LATAM",
    languageKey: "latam",
    name: "latam",
  },
  fr: {
    icon: "locales/fr",
    shortName: "FR",
    languageKey: "fr",
    name: "french",
  },
  tr: {
    icon: "locales/tr",
    shortName: "TR",
    languageKey: "tr",
    name: "turkish",
  },
  ar: {
    icon: "locales/ar",
    shortName: "AR",
    languageKey: "ar",
    name: "arabic",
  },
  uk: {
    icon: "locales/uk",
    shortName: "UK",
    languageKey: "uk",
    name: "ukrainian",
  },
  ru: {
    icon: "locales/ru",
    shortName: "RU",
    languageKey: "ru",
    name: "russian",
  },
  pl: {
    icon: "locales/pl",
    shortName: "PL",
    languageKey: "pl",
    name: "polish",
  },
  cs: {
    icon: "locales/cs",
    shortName: "CS",
    languageKey: "cs",
    name: "czech",
  },
  it: {
    icon: "locales/it",
    shortName: "IT",
    languageKey: "it",
    name: "italian",
  },
  nl: {
    icon: "locales/nl",
    shortName: "NL",
    languageKey: "nl",
    name: "dutch",
  },
};

const LanguageSettings: FC<LanguageSettingsProps> = ({ app, project }) => {
  const { i18n, t } = useTranslation();
  const { closeModal } = useModals();
  const { user } = useAuthContext();

  const { fetchTranslations } = useGetTranslations(project);
  const [_, setTranslation] = useGlobalState().translationsState;

  const finalLocales =
    app === "dashboard"
      ? // For the dashboard app, exclude certain locales
        user?.isSuperAdmin
        ? Object.keys(locales).filter((locale) => locale !== "ar")
        : Object.keys(locales).filter(
            (locale) => locale === "de" || locale === "en",
          )
      : // For other apps, include locales based on project settings
        Object.keys(locales).filter(
          (locale) =>
            project?.settings?.originalLanguage === locale ||
            project?.settings?.languages?.includes(locale),
        );
  return (
    <div className="p-5 pt-0">
      <List
        borderless={true}
        data={finalLocales
          .map((locale) => {
            return (
              <ListItem
                key={locale}
                onClick={() => {
                  if (locale === project?.settings?.originalLanguage) {
                    setTranslation(null);
                  }
                  if (app === "dashboard") {
                    localStorage.setItem("dashboardLanguage", locale);
                  } else {
                    localStorage.setItem("language", locale);
                  }

                  i18n.changeLanguage(locale);
                  i18n.reloadResources();
                  fetchTranslations(locale);
                  closeModal();
                }}
                id={locales[locale].languageKey}
                tag="div"
                selected={i18n.resolvedLanguage === locale}
                primaryContent={t(
                  `languages:${locales[locale].languageKey}.${locales[locale].languageKey}`,
                )}
                aria-label={t(`languages:${locale}.${i18n.resolvedLanguage}`)}
                leadingIcon={locales[locale].icon}
              />
            );
          })
          .filter(Boolean)}
      />
    </div>
  );
};

export default LanguageSettings;
